import { z } from 'zod';

import { CatalogApplicabilitySchema } from '../product/catalog-search.schemas.js';
import { UsefulLifeDueStatusSchema } from '../vehicle/useful-life-due.schemas.js';

import { ReceptionDamageSchema } from './reception-damage.schemas.js';
import {
  PartLinePriceOriginSchema,
  SuggestedPaymentMethodSchema,
} from './service-quote.schemas.js';

export {
  PART_LINE_PRICE_ORIGINS,
  PartLinePriceOriginSchema,
  type PartLinePriceOrigin,
} from './service-quote.schemas.js';

export const QUOTE_STATUSES = [
  'DRAFT',
  'SENT',
  'PARTIALLY_APPROVED',
  'APPROVED',
  'REJECTED',
  'EXPIRED',
  'CANCELLED',
] as const;

export const ListQuotesQuerySchema = z.object({
  status: z.enum(QUOTE_STATUSES).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export type ListQuotesQuery = z.infer<typeof ListQuotesQuerySchema>;

export const QuoteListItemSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  receptionId: z.uuid().nullable(),
  partyId: z.uuid().nullable(),
  customerRoleId: z.uuid().nullable(),
  customerVehicleId: z.uuid().nullable(),
  containerLabel: z.string().nullable(),
  partyDisplayName: z.string(),
  vehiclePlate: z.string().nullable(),
  vehiclePlateNormalized: z.string().nullable(),
  vehicleDisplayName: z.string(),
  version: z.number().int().positive(),
  status: z.enum(QUOTE_STATUSES),
  totalCents: z.string(),
  currency: z.string(),
  sentAt: z.iso.datetime().nullable(),
  validUntil: z.string().nullable(),
  displayNumber: z.string(),
  openedAt: z.iso.datetime(),
  correctsServiceOrderId: z.uuid().nullable(),
  correctsServiceOrderDisplayNumber: z.string().nullable(),
});

export type QuoteListItem = z.infer<typeof QuoteListItemSchema>;

export const ListQuotesResponseSchema = z.object({
  items: z.array(QuoteListItemSchema),
  pagination: z.object({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
    limit: z.number().int(),
  }),
});

export type ListQuotesResponse = z.infer<typeof ListQuotesResponseSchema>;

/** BR-SO-79 — purchase readiness for quote assembly (RFQ before send). */
export const QuoteItemPurchaseReadinessSchema = z.enum([
  'IN_STOCK',
  'NEEDS_RFQ',
  'RFQ_SELECTED',
  'NOT_APPLICABLE',
]);

export type QuoteItemPurchaseReadiness = z.infer<typeof QuoteItemPurchaseReadinessSchema>;

/**
 * BR-SO-153 — live reading of a quote/OS line at request time. Never persisted on the line;
 * `unitCostCents` here is the catalog/stock reading, distinct from the line snapshot.
 */
export const LineOperationalReadingSchema = z.object({
  unitCostCents: z.string().nullable(),
  stockQuantity: z.string().nullable(),
  usefulLifeMeters: z.number().int().nullable(),
  usefulLifeMonths: z.number().int().nullable(),
  usefulLifeDueStatus: UsefulLifeDueStatusSchema.nullable(),
  applicability: CatalogApplicabilitySchema,
});

export type LineOperationalReading = z.infer<typeof LineOperationalReadingSchema>;

export const QuoteItemDetailSchema = z.object({
  id: z.uuid(),
  itemType: z.string(),
  quantity: z.string(),
  unitPriceCents: z.string().nullable(),
  unitCostCents: z.string().nullable().optional(),
  priceOrigin: PartLinePriceOriginSchema.nullable().optional(),
  rfqUnitPriceCents: z.string().nullable().optional(),
  totalCents: z.string(),
  currency: z.string(),
  approvedByCustomer: z.boolean(),
  rejectedByCustomer: z.boolean(),
  dependsOnQuoteItemId: z.uuid().nullable(),
  itemSnapshot: z.record(z.string(), z.unknown()),
  purchaseReadiness: QuoteItemPurchaseReadinessSchema,
  manufacturerName: z.string().nullable().optional(),
  manufacturerCode: z.string().nullable().optional(),
  categoryName: z.string().nullable().optional(),
  subcategoryName: z.string().nullable().optional(),
  quotedSupplierName: z.string().nullable().optional(),
  /** BR-SO-217 — operator-chosen supplier role when set. */
  supplierRoleId: z.uuid().nullable().optional(),
  /** BR-SO-218 — operator-typed direct-supply identity. */
  directPartName: z.string().nullable().optional(),
  complementaryOfName: z.string().nullable().optional(),
  directManufacturerName: z.string().nullable().optional(),
  directManufacturerCode: z.string().nullable().optional(),
  manufacturerCodeOptional: z.boolean().optional(),
  operationalReading: LineOperationalReadingSchema.optional(),
});

export type QuoteItemDetail = z.infer<typeof QuoteItemDetailSchema>;

export const GetQuoteResponseSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  receptionId: z.uuid().nullable(),
  partyId: z.uuid().nullable(),
  customerRoleId: z.uuid().nullable(),
  customerVehicleId: z.uuid().nullable(),
  containerLabel: z.string().nullable(),
  partyDisplayName: z.string(),
  partyPrimaryPhone: z.string().nullable(),
  partyPrimaryEmail: z.string().nullable(),
  vehiclePlate: z.string().nullable(),
  vehiclePlateNormalized: z.string().nullable(),
  vehicleDisplayName: z.string(),
  entryOdometerKm: z.number().int().nullable(),
  reportedIssue: z.string().nullable(),
  notes: z.string().nullable(),
  fuelLevelEighths: z.number().int().min(0).max(8).nullable(),
  receptionDamages: z.array(ReceptionDamageSchema),
  attachments: z.array(
    z.object({
      id: z.uuid(),
      origin: z.enum(['CHECK_IN', 'QUOTE']),
      mediaType: z.enum(['IMAGE', 'VIDEO']).default('IMAGE'),
      contentType: z.string().min(1).nullable().default(null),
      objectUrl: z.string(),
      caption: z.string().nullable(),
      alt: z.string(),
    }),
  ),
  openedAt: z.iso.datetime(),
  version: z.number().int().positive(),
  status: z.enum(QUOTE_STATUSES),
  totalCents: z.string(),
  currency: z.string(),
  sentAt: z.iso.datetime().nullable(),
  validUntil: z.string().nullable(),
  approvedAt: z.iso.datetime().nullable(),
  rejectedAt: z.iso.datetime().nullable(),
  suggestedPaymentMethod: SuggestedPaymentMethodSchema.nullable(),
  diagnosticText: z.string().nullable(),
  technicalObservations: z.string().nullable(),
  receptionHasServiceOrder: z.boolean(),
  serviceOrderId: z.uuid().nullable(),
  sellerUserId: z.uuid().nullable(),
  correctsServiceOrderId: z.uuid().nullable(),
  correctsServiceOrderDisplayNumber: z.string().nullable(),
  correctionWouldBeWarrantyClaim: z.boolean(),
  displayNumber: z.string(),
  receptionDisplayNumber: z.string().nullable(),
  purchaseQuoteId: z.uuid().nullable().optional(),
  items: z.array(QuoteItemDetailSchema),
});

export type GetQuoteResponse = z.infer<typeof GetQuoteResponseSchema>;

export const ListServiceOrdersQuerySchema = z.object({
  status: z.string().optional(),
  partyId: z.uuid().optional(),
  customerVehicleId: z.uuid().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export type ListServiceOrdersQuery = z.infer<typeof ListServiceOrdersQuerySchema>;

export const ServiceOrderListItemSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  receptionId: z.uuid().nullable(),
  serviceQuoteId: z.uuid().nullable(),
  partyId: z.uuid().nullable(),
  customerRoleId: z.uuid().nullable(),
  customerVehicleId: z.uuid().nullable(),
  partyDisplayName: z.string(),
  vehiclePlate: z.string().nullable(),
  vehiclePlateNormalized: z.string().nullable(),
  containerLabel: z.string().nullable(),
  status: z.string(),
  totalCents: z.string(),
  currency: z.string(),
  openedAt: z.iso.datetime(),
  displayNumber: z.string(),
  vehicleDisplayName: z.string().optional(),
  /** Serviço em execução nesta OS, quando houver. */
  runningServiceName: z.string().nullable().optional(),
  runningMechanicName: z.string().nullable().optional(),
  runningStartedAt: z.iso.datetime().nullable().optional(),
  runningWorkedSeconds: z.number().int().nonnegative().optional(),
  serviceCount: z.number().int().nonnegative().optional(),
  doneServiceCount: z.number().int().nonnegative().optional(),
  yardColumnName: z.string().nullable().default(null),
  yardColumnColor: z.string().nullable().default(null),
});

export type ServiceOrderListItem = z.infer<typeof ServiceOrderListItemSchema>;

export const ListServiceOrdersResponseSchema = z.object({
  items: z.array(ServiceOrderListItemSchema),
  pagination: z.object({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
    limit: z.number().int(),
  }),
});

export type ListServiceOrdersResponse = z.infer<typeof ListServiceOrdersResponseSchema>;

export const ServiceOrderPlaceSummaryByStatusSchema = z.object({
  status: z.string(),
  count: z.number().int().nonnegative(),
  totalCents: z.string(),
});

export const ServiceOrderPlaceSummaryResponseSchema = z.object({
  byStatus: z.array(ServiceOrderPlaceSummaryByStatusSchema),
  todayDeliveredCount: z.number().int().nonnegative(),
  todayTicketAverageCents: z.string(),
});

export type ServiceOrderPlaceSummaryResponse = z.infer<
  typeof ServiceOrderPlaceSummaryResponseSchema
>;
